/**
 * Passive system-health poller.
 *
 * Hits `/api/health/deep` on a coarse (2 min) interval to detect if any
 * downstream (Mongo / R2 / Resend) has gone red. Consumers use the
 * returned `hasIssue` flag to render alert affordances (e.g. the red dot
 * on the sidebar Settings icon) without forcing the operator to open the
 * dashboard first.
 *
 * The poll cadence is intentionally slow — every hit performs a real R2
 * write + Resend API call, so 60 s cadence would waste quotas on idle
 * admin tabs. If real-time alerting becomes a requirement, wire this to
 * a WebSocket / SSE stream instead of shortening the interval.
 *
 * Non-admins get `{hasIssue:false, loading:false}` — the endpoint is
 * admin-gated (403) and calling it from a cashier session would just
 * pollute the console with errors.
 */
import { useEffect, useState } from 'react';

import { healthAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL_MS = 2 * 60 * 1000;   // 2 minutes

export function useSystemHealth() {
    const { isAdmin } = useAuth();
    const [state, setState] = useState({ hasIssue: false, loading: false, data: null });

    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        const probe = async () => {
            try {
                const { data } = await healthAPI.deep();
                if (cancelled) return;
                const hasIssue = !(
                    data?.shallow?.db_ok
                    && data?.resend?.ok
                    && data?.r2_write?.ok
                    && data?.mongo?.ok
                );
                setState({ hasIssue, loading: false, data });
            } catch {
                // 403 (non-admin) or 5xx — surface as issue only if the caller
                // is actually an admin (isAdmin gate above already ensures this).
                if (cancelled) return;
                setState({ hasIssue: true, loading: false, data: null });
            }
        };
        probe();
        const timer = setInterval(probe, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [isAdmin]);

    return state;
}
